import {
  AlertCircle,
  AlertTriangle,
  Boxes,
  Building2,
  Check,
  ChevronRight,
  Gauge,
  Network,
  Rocket,
  ServerCog,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Tag,
  Tv,
  Wallet,
  X,
  type LucideIcon,
} from "lucide-react";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function OwnStbVsSmartTvPage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const salesCta = u("/contacts?inquiry=sales#inquiry-form");

  const heroStats = [
    { icon: Tag, label: messages.osStat1 },
    { icon: SlidersHorizontal, label: messages.osStat2 },
    { icon: Tv, label: messages.osStat3 },
  ];

  const ownItems = [
    messages.osOwnItem1,
    messages.osOwnItem2,
    messages.osOwnItem3,
    messages.osOwnItem4,
    messages.osOwnItem5,
    messages.osOwnItem6,
  ];
  const rentItems = [
    messages.osRentItem1,
    messages.osRentItem2,
    messages.osRentItem3,
    messages.osRentItem4,
    messages.osRentItem5,
    messages.osRentItem6,
  ];

  const benefits: { icon: LucideIcon; title: string; body: string }[] = [
    { icon: Tag, title: messages.osBenefit1Title, body: messages.osBenefit1Body },
    { icon: ShieldCheck, title: messages.osBenefit2Title, body: messages.osBenefit2Body },
    { icon: ServerCog, title: messages.osBenefit3Title, body: messages.osBenefit3Body },
    { icon: Gauge, title: messages.osBenefit4Title, body: messages.osBenefit4Body },
    { icon: Rocket, title: messages.osBenefit5Title, body: messages.osBenefit5Body },
    { icon: Tv, title: messages.osBenefit6Title, body: messages.osBenefit6Body },
    { icon: Wallet, title: messages.osBenefit7Title, body: messages.osBenefit7Body },
    { icon: Building2, title: messages.osBenefit8Title, body: messages.osBenefit8Body },
    { icon: Network, title: messages.osBenefit9Title, body: messages.osBenefit9Body },
  ];

  const hidden = [messages.osHidden1, messages.osHidden2, messages.osHidden3, messages.osHidden4];

  const faqs = [
    { q: messages.osFaqQ1, a: messages.osFaqA1 },
    { q: messages.osFaqQ2, a: messages.osFaqA2 },
    { q: messages.osFaqQ3, a: messages.osFaqA3 },
    { q: messages.osFaqQ4, a: messages.osFaqA4 },
    { q: messages.osFaqQ5, a: messages.osFaqA5 },
    { q: messages.osFaqQ6, a: messages.osFaqA6 },
  ];

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={u("/guides")} className="hover:text-foreground">
            {messages.navGuides}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.osBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div aria-hidden className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-40" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[440px] w-[600px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            {messages.osHeroEyebrow}
          </div>
          <h1
            className="mt-4 animate-fade-up text-balance text-4xl font-semibold tracking-tight sm:text-5xl"
            style={{ animationDelay: "80ms" }}
          >
            {messages.osHeroTitle}{" "}
            <span className="text-gradient">{messages.osHeroTitleAccent}</span>
          </h1>
          <p
            className="mx-auto mt-6 max-w-2xl animate-fade-up text-pretty text-lg text-muted-foreground"
            style={{ animationDelay: "160ms" }}
          >
            {messages.osHeroSub}
          </p>
          <p
            className="mt-5 animate-fade-up text-pretty text-sm font-medium text-foreground/80"
            style={{ animationDelay: "220ms" }}
          >
            {messages.osHeroNote}
          </p>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-3"
            style={{ animationDelay: "280ms" }}
          >
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.osHeroCtaPrimary}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <a href="#control">{messages.osHeroCtaSecondary}</a>
            </Button>
          </div>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-2"
            style={{ animationDelay: "340ms" }}
          >
            {heroStats.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
              >
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thesis — own the core, reach everywhere */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.osThesisEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.osThesisTitle}
          </h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>{messages.osThesisBody1}</p>
            <p>{messages.osThesisBody2}</p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-[1.15fr_1fr]">
            <div className="rounded-2xl border border-primary/30 bg-card/70 p-5 shadow-glow">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Boxes className="h-4 w-4" />
                {messages.osThesisCoreLabel}
              </div>
              <p className="mt-2 text-lg font-semibold text-foreground">
                {messages.osThesisCoreValue}
              </p>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card/40 p-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Share2 className="h-4 w-4" />
                {messages.osThesisReachLabel}
              </div>
              <p className="mt-2 text-base font-medium text-foreground/90">
                {messages.osThesisReachValue}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* You own vs. you rent */}
      <RevealSection id="control" className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.osOwnRentEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.osOwnRentTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-primary/30 bg-card/80 p-6 shadow-glow sm:p-7">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <h3 className="text-base font-semibold text-foreground">{messages.osOwnColTitle}</h3>
              </div>
              <ul className="mt-5 space-y-3">
                {ownItems.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground/90">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card/40 p-6 sm:p-7">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                  <Building2 className="h-5 w-5" />
                </span>
                <h3 className="text-base font-semibold text-foreground">
                  {messages.osRentColTitle}
                </h3>
              </div>
              <ul className="mt-5 space-y-3">
                {rentItems.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* Benefits grid */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.osBenefitsEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.osBenefitsTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map(({ icon: Icon, title, body }) => (
              <article key={title} className="rounded-2xl border border-border/60 bg-card/60 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* Sovereignty scenario */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="rounded-2xl border border-primary/25 bg-card/70 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                  {messages.osScenarioTitle}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  {messages.osScenarioBody}
                </p>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* The hidden cost of Smart-TV-only */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.osHiddenEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.osHiddenTitle}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {messages.osHiddenBody}
          </p>
          <ul className="mt-6 space-y-3">
            {hidden.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-3"
              >
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <span className="text-sm text-foreground/90">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </RevealSection>

      {/* Where we fit */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.osSameEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.osSameTitle}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">
            {messages.osSameBody}
          </p>
          <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-primary/20 bg-primary/[0.04] p-6 sm:flex-row sm:items-center">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Boxes className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-base font-semibold text-foreground">{messages.osSameNoteTitle}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {messages.osSameNoteBody}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.osFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.osFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-16 text-background sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.osCtaEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.osCtaTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.osCtaBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.osCtaPrimary}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background sm:w-auto"
            >
              <a href={salesCta}>{messages.osCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}
